/* eslint-disable no-inline-styles/no-inline-styles */
import { OnboardingEvents, trackTagManagerEvent } from 'firebase-client';
import { Checkbox, FormProvider, SubmitHandler, TextInput, useForm, yupResolver } from 'forms';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Form, Link } from 'ui';
import { boolean, object, string } from 'yup';

import { isRegistered } from '~/auth';
import { useCreateAccount } from '~/onboarding';
import { ContinueButton, StepTitle } from '~/ui';

interface FormValues {
  email: string;
  gdpr: boolean;
  isSubscribedForMarketing?: boolean;
  terms: boolean;
}

export const EmailStep = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const {
    goToNextStep,
    setEmail,
    setIsSubscribedForMarketing,
    accountData: { email, isSubscribedForMarketing },
  } = useCreateAccount();

  const resolver = yupResolver(
    object({
      email: string()
        .required(t('validation:required'))
        .email(t('validation:invalidEmail'))
        .test('isRegistered', t('register:steps:email:emailAlreadyInUse'), async (value) => {
          if (!value) return true;

          setIsLoading(true);
          // Check if the email is valid before performing the async test
          const isEmailValid = string().email().isValidSync(value);

          if (!isEmailValid) {
            setIsLoading(false);
            return true;
          }

          const registered = await isRegistered(value);
          setIsLoading(false);
          return !registered;
        }),
      gdpr: boolean().oneOf([true], t('validation:required')).required(),
      isSubscribedForMarketing: boolean(),
      terms: boolean().oneOf([true], t('validation:required')).required(),
    })
  );

  const { handleSubmit, watch, formState, getValues, ...rest } = useForm<FormValues>({
    defaultValues: { email, gdpr: false, isSubscribedForMarketing, terms: false },
    mode: 'onChange',
    resolver,
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    trackTagManagerEvent(OnboardingEvents.email);

    setEmail(values.email);
    setIsSubscribedForMarketing(values.isSubscribedForMarketing ?? false);
    goToNextStep();
  };

  const { gdpr, terms, email: emailValue } = getValues();
  const isFormCompleted = gdpr && terms && Boolean(emailValue);

  return (
    <FormProvider {...{ formState, getValues, handleSubmit, watch, ...rest }}>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Box height="100%" display="flex" flexDirection="column">
          <StepTitle>{t('register:steps:email:title')}</StepTitle>

          <Box display="flex" flexDirection="column" justifyContent="space-between" flexGrow={1}>
            <Box>
              <TextInput
                required
                margin="normal"
                fullWidth
                label={t('email')}
                autocomplete="email"
                name="email"
                shrink
                data-testid="email"
              />

              <Checkbox
                sx={{ mt: 1 }}
                label={
                  <>
                    {`${t('register:readAndAgree')} `}
                    <Link href={t('register:files:terms')} newTab>
                      {t('register:termsOfUseHyperlink')}
                    </Link>
                  </>
                }
                required
                name="terms"
                data-testid="terms"
              />
              <Checkbox
                sx={{ mt: 1 }}
                label={
                  <>
                    {`${t('register:dataProceeding', { subdomain: 'signup' })} `}
                    <Link href={t('register:files:gdpr')} newTab>
                      {t('register:personalDataPolicy')}
                    </Link>
                  </>
                }
                required
                name="gdpr"
                data-testid="gdpr"
              />
              <Checkbox
                sx={{ mt: 1 }}
                label={t('register:marketingMessages')}
                name="isSubscribedForMarketing"
                data-testid="isSubscribedForMarketing"
              />
            </Box>

            <ContinueButton
              data-testid="account-email-submit-button"
              loading={isLoading && isFormCompleted}
              disabled={!formState.isValid}
            />
          </Box>
        </Box>
      </Form>
    </FormProvider>
  );
};
